import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { LuckyBoxGrabStatus, type SessionStoreInstance } from '@halo/chat-sdk-react';
import { Avatar, CloseIcon } from '@halo/ui-chat-react';
import { Dialog } from '@halo/ui-react';
import { BIF } from '@halo/wallet-app';
import dayjs from 'dayjs';
import { useStore } from 'zustand';
import { MyTrans } from '@halo/ui-react';
import type { ActionsStore } from '@/store/actions';
import luckyBoxDetailsHeader from '@/assets/images/luckyBox/luckyBoxDetailsHeader.png';
import empty from '@/assets/images/luckyBox/empty.png';
import type { GrabInfo } from './GrabList';
import { GrabList } from './GrabList';
import { formatTimeDifferenceDetailed } from './LuckyTabs/helpers';

interface LuckyDetailsDialogProps {
  isOpen?: boolean;
  sessionStoreInstance: SessionStoreInstance;
  luckyDetailsDialogData: ActionsStore['luckyDetailsDialogData'];
  setIsOpen?: (isOpen: boolean) => void;
}

export function LuckyDetailsDialog({
  isOpen,
  sessionStoreInstance,
  luckyDetailsDialogData,
  setIsOpen,
}: LuckyDetailsDialogProps) {
  const { user, currentUser, message: { payload } = {} } = luckyDetailsDialogData || {};
  const sessionStore = useStore(sessionStoreInstance);
  const { t } = useTranslation();

  const [open, setOpen] = useState(isOpen);
  const [grabInfo, setGrabInfo] = useState<GrabInfo>({
    claimedCount: 0,
    finishedDuration: 0,
    claimMembers: [],
    isClaimedEnd: false,
  });

  const claimTotal = useMemo(() => {
    return grabInfo.claimMembers
      ?.reduce((total, member) => total.sum(member.amount), new BIF(0))
      .toString();
  }, [grabInfo.claimMembers]);

  const isExpired = useMemo(() => {
    return (
      payload?.deadline < dayjs().valueOf() || payload?.grabStatus === LuckyBoxGrabStatus.expired
    );
  }, [payload?.deadline, payload?.grabStatus]);

  const onGrabInfo = useCallback((value: GrabInfo) => {
    setGrabInfo((prev) => ({ ...prev, ...value }));
  }, []);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setIsOpen?.(false);
  }, [setIsOpen]);

  useLayoutEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <Dialog.Root
      placement="center"
      open={open}
      onOpenChange={(details) => {
        setOpen(details.open);
        setIsOpen?.(details.open);
      }}
      lazyMount
      unmountOnExit
      closeOnInteractOutside={false}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner closeOnInteractOutside={false}>
        <Dialog.Content
          borderRadius="2xl"
          w="444px"
          h="700px"
          px="5"
          py="4"
          bg="bg.subtle"
          bgImage={`url(${luckyBoxDetailsHeader})`}
          bgRepeat="no-repeat"
          bgSize="contain"
          display="flex"
          direction="column"
        >
          <Dialog.Header
            fontSize="md"
            fontWeight="semibold"
            justifyContent="flex-end"
            flex="none"
            p="0"
          >
            <Box color="fg.muted" cursor="pointer" fontSize="xl" onClick={handleCancel}>
              <CloseIcon />
            </Box>
          </Dialog.Header>
          <Dialog.Body p="0" mt="4" flex="1" overflow="hidden">
            <Flex direction="column" h="full">
              <Flex flex="none" mt="11px" justifyContent="center" gap="2" alignItems="center">
                <Avatar w="6" h="6" borderRadius="full" src={user?.avatar} />
                <Flex fontWeight="semibold">
                  <MyTrans
                    i18nKey="chat.whoSentALuckyBox"
                    components={{
                      components: <Text maxW="52" truncate mr="1" />,
                    }}
                    values={{
                      name: user?.name,
                    }}
                  />
                </Flex>
              </Flex>
              <Flex
                flex="none"
                justifyContent="center"
                mt="3"
                direction="column"
                alignItems="center"
              >
                {(grabInfo?.isClaimedEnd || isExpired) &&
                payload?.grabStatus !== LuckyBoxGrabStatus.claimed ? (
                  <>
                    <Image src={empty} w="100px" h="100px" />
                    <Text fontWeight="medium" fontSize="sm" color="fg.muted" mt="2">
                      {grabInfo?.isClaimedEnd
                        ? t('chat.luckyBoxClaimed')
                        : isExpired
                          ? t('chat.luckyBoxExpired')
                          : ''}
                    </Text>
                  </>
                ) : (
                  <>
                    <Box width="14" height="14" position="relative">
                      <Image
                        src={payload?.tokenIcon}
                        width="14"
                        height="14"
                        borderRadius="full"
                      />
                      <Image
                        src={payload?.chainIcon}
                        alt="info"
                        width="6"
                        height="6"
                        borderRadius="full"
                        w="24.615px"
                        h="24.615px"
                        position="absolute"
                        bottom="-0.115px"
                        right="0.385px"
                        borderWidth="1.231px"
                        borderColor="gray.10"
                        borderStyle="solid"
                      />
                    </Box>
                    <Text mt="3" fontSize="5xl" fontWeight="bold">
                      {payload?.grabAmount} {payload?.symbol}
                    </Text>
                    <Text mt="2" fontSize="xs" fontWeight="medium" color="fg.muted">
                      {payload?.boxDesc}
                    </Text>
                  </>
                )}
              </Flex>
              <Flex
                flex="1"
                mt="4"
                direction="column"
                bg="white"
                borderRadius="xl"
                overflow="hidden"
              >
                <Flex flex="none" mx="3" my="2.5" gap="2" fontSize="xs">
                  <Box px="1" py="0.5" borderRadius="sm" bg="bg.subtle">
                    {t('chat.claimed')}: {grabInfo?.claimedCount}/{payload?.quantity}
                  </Box>
                  {user?.user_id === currentUser?.user_id && (
                    <Box px="1" py="0.5" borderRadius="sm" bg="bg.subtle">
                      {t('chat.totaling')}: {claimTotal}/{payload?.amount} {payload?.symbol}
                    </Box>
                  )}
                  {grabInfo?.isClaimedEnd && (
                    <Box px="1" py="0.5">
                      {t('chat.claimed')}:
                      {formatTimeDifferenceDetailed(grabInfo?.finishedDuration)}
                    </Box>
                  )}
                  {!grabInfo?.isClaimedEnd && isExpired && (
                    <Box px="1" py="0.5" borderRadius="sm" bg="bg.subtle">
                      {t('chat.expiredLuckyBox')}
                    </Box>
                  )}
                </Flex>
                <Box flex="1" overflowY="scroll">
                  <GrabList
                    payload={payload}
                    sessionStore={sessionStore}
                    onGrabInfo={onGrabInfo}
                  />
                </Box>
              </Flex>
            </Flex>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
